import * as React from 'react';
import { AbsenceEditFormData, AbsenceType, AbsenceStatus } from '../services/AbsenceService';
import styles from './Absences.module.css';

interface EmployeeOption {
  id: number;
  text: string;
}

interface AbsenceEditModalProps {
  editData: AbsenceEditFormData;
  setEditData: React.Dispatch<React.SetStateAction<AbsenceEditFormData>>;
  empSelect: EmployeeOption[];
  onClose: () => void;
  onSave: () => void;
}

export function AbsenceEditModal({
  editData,
  setEditData,
  empSelect,
  onClose,
  onSave,
}: AbsenceEditModalProps): React.JSX.Element {
  return (
    <div
      data-testid="edit-modal-overlay"
      className={styles.modalOverlay}
      onClick={onClose}
    >
      <div
        data-testid="edit-modal-content"
        onClick={(e) => e.stopPropagation()}
        className={styles.modalContent}
      >
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Edit Absence</h2>
          <button onClick={onClose} className={styles.modalCloseBtn}>
            &#10005;
          </button>
        </div>

        {/* Form */}
        <div className={styles.modalForm}>
          {/* Employee & Status */}
          <div className={styles.modalFormRow}>
            <div className={styles.modalFormCol}>
              <label className={styles.glassLabel}>Employee</label>
              <select
                value={editData.employee}
                onChange={(e) => setEditData({ ...editData, employee: e.target.value })}
                className={styles.glassInput}
              >
                <option value="">Select Employee</option>
                {empSelect.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.text}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.modalFormCol}>
              <label className={styles.glassLabel}>Status</label>
              <select
                value={editData.status}
                onChange={(e) => setEditData({ ...editData, status: e.target.value as AbsenceStatus })}
                className={styles.glassInput}
              >
                <option value="REQUESTED">Requested</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Type */}
          <div>
            <label className={styles.glassLabel}>Absence Type</label>
            <select
              value={editData.absence_type}
              onChange={(e) => setEditData({ ...editData, absence_type: e.target.value as AbsenceType })}
              className={styles.glassInput}
            >
              <option value="HOLIDAY">Holiday</option>
              <option value="OVERTIME">Overtime</option>
              <option value="SICK">Sick</option>
              <option value="SICK_CHILD">Sick Child</option>
              <option value="EXAM">Exam</option>
              <option value="WISH_OFF">Wish Off</option>
            </select>
          </div>

          {/* Start & End Date */}
          <div className={styles.modalFormRow}>
            <div className={styles.modalFormCol}>
              <label className={styles.glassLabel}>Start Date</label>
              <input
                type="date"
                value={editData.start_date}
                onChange={(e) => setEditData({ ...editData, start_date: e.target.value })}
                className={styles.glassInput}
              />
            </div>
            <div className={styles.modalFormCol}>
              <label className={styles.glassLabel}>End Date</label>
              <input
                type="date"
                value={editData.end_date}
                onChange={(e) => setEditData({ ...editData, end_date: e.target.value })}
                className={styles.glassInput}
              />
            </div>
          </div>

          {/* Half Day Toggles */}
          <div className={styles.halfDayRow}>
            <label className={styles.halfDayLabel}>
              <input
                type="checkbox"
                checked={!editData.full_day_start}
                onChange={(e) => setEditData({ ...editData, full_day_start: !e.target.checked })}
                style={{ width: 'auto', accentColor: '#E7007D' }}
              />
              Erster Tag halber Tag
            </label>
            <label className={styles.halfDayLabel}>
              <input
                type="checkbox"
                checked={!editData.full_day_end}
                onChange={(e) => setEditData({ ...editData, full_day_end: !e.target.checked })}
                style={{ width: 'auto', accentColor: '#E7007D' }}
              />
              Letzter Tag halber Tag
            </label>
          </div>

          {/* Time fields for partial days */}
          {(!editData.full_day_start || !editData.full_day_end) && (
            <div className={styles.timeFieldsRow}>
              {!editData.full_day_start && (
                <div className={styles.timeFieldGroup}>
                  <div className={styles.timeFieldCol}>
                    <label className={styles.glassLabel}>Von</label>
                    <input
                      type="time"
                      value={editData.start_day_start_time}
                      onChange={(e) => setEditData({ ...editData, start_day_start_time: e.target.value })}
                      className={styles.glassInput}
                    />
                  </div>
                  <div className={styles.timeFieldCol}>
                    <label className={styles.glassLabel}>Bis</label>
                    <input
                      type="time"
                      value={editData.start_day_end_time}
                      onChange={(e) => setEditData({ ...editData, start_day_end_time: e.target.value })}
                      className={styles.glassInput}
                    />
                  </div>
                </div>
              )}
              {!editData.full_day_end && editData.start_date !== editData.end_date && (
                <div className={styles.timeFieldGroup}>
                  <div className={styles.timeFieldCol}>
                    <label className={styles.glassLabel}>Von</label>
                    <input
                      type="time"
                      value={editData.end_day_start_time}
                      onChange={(e) => setEditData({ ...editData, end_day_start_time: e.target.value })}
                      className={styles.glassInput}
                    />
                  </div>
                  <div className={styles.timeFieldCol}>
                    <label className={styles.glassLabel}>Bis</label>
                    <input
                      type="time"
                      value={editData.end_day_end_time}
                      onChange={(e) => setEditData({ ...editData, end_day_end_time: e.target.value })}
                      className={styles.glassInput}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Reason */}
          <div>
            <label className={styles.glassLabel}>Reason (optional)</label>
            <textarea
              value={editData.reason_note}
              onChange={(e) => setEditData({ ...editData, reason_note: e.target.value })}
              placeholder="Add a note..."
              className={styles.glassTextarea}
            />
          </div>
        </div>

        {/* Footer Buttons */}
        <div className={styles.modalFooter}>
          <button
            data-testid="edit-modal-cancel-button"
            onClick={onClose}
            className={styles.cancelBtn}
          >
            Cancel
          </button>
          <button
            data-testid="edit-modal-update-button"
            onClick={onSave}
            className={styles.saveBtn}
          >
            Update
          </button>
        </div>
      </div>
    </div>
  );
}
